import Cookies from 'js-cookie'
import ky from 'ky'
import React from 'react'
import styled from 'styled-components'

import {DynamicForm, FieldType, FieldWidth, parsePhoneNumber} from '@/components/dynamic-form'
import {paramCookiesMap} from '@/config/cookies'
import {useGlobalStrings} from '@/providers/global-strings'
import {container} from '@/styles/grid'
import mq from '@/utils/media-query'

import type {FieldProps, InputProps, State} from '@/components/dynamic-form'
import type {SingleJobQuery} from '@/graphql-types'
import type {GlobalStrings} from '@/types/global-strings'
import type {PageProps} from '@/types/page-props'
import type {CountryCode as PhoneCountryCode} from 'libphonenumber-js'

type GreenhouseQuestions =
    PageProps<SingleJobQuery>['data']['allGreenhouseJob']['nodes'][number]['questions']

interface Props {
    jobId: string
    questions: GreenhouseQuestions
    setIsSubmitted: (status: boolean) => void
}

interface Payload {
    jobId: string
    phone: string
    first_name: string
    last_name: string
    email: string
    [key: string]: string | unknown | undefined
}

const toBase64 = async (file: File) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result)
        reader.onerror = (error) => reject(error)
    })

export const ApplicationForm = ({jobId, questions, setIsSubmitted}: Props) => {
    const globalStrings = useGlobalStrings()
    const fields = mapFields(questions, globalStrings)
    const [apiErrorMsg, setApiErrorMsg] = React.useState('')

    // Add the Consent field, since it isn't included in the Greenhouse fields
    fields.push({
        key: 'consent',
        type: FieldType.Checkbox,
        label: globalStrings.accept_terms_and_privacy,
        validators: ['isRequired'],
        errorMessage: globalStrings.this_field_is_required,
        width: FieldWidth.Full,
    })

    const submitForm = async (state: State) => {
        let resumeFile = state.resume as File
        let resumeResult = null

        if (resumeFile) {
            resumeResult = await toBase64(resumeFile)
        }

        let coverLetterFile = state.cover_letter as File
        let coverLetterResult = null

        if (coverLetterFile) {
            coverLetterResult = await toBase64(coverLetterFile)
        }

        const {
            'phone-phoneNumber': phonePhoneNumber,
            'phone-countryCode': phoneCountryCode,
            resume,
            cover_letter,
            ...rest
        } = state

        const payload: Partial<Payload> = {
            ...rest,
            jobId,
            phone: parsePhoneNumber(
                state['phone-phoneNumber'] as string,
                state['phone-countryCode'] as PhoneCountryCode,
            )?.number as string,
            ...(resumeFile && {
                resume: resumeResult,
                resume_filename: resumeFile.name,
            }),
            ...(coverLetterFile && {
                cover_letter: coverLetterResult,
                cover_letter_filename: coverLetterFile.name,
            }),
            mapped_url_token: Cookies.get(paramCookiesMap.latestGreenHouseSource),
        }

        try {
            const response = await ky.post('/api/greenhouse-submit-application', {
                json: payload,
            })

            if (response.ok) {
                setIsSubmitted(true)
            }
        } catch (error: any) {
            if (error.name === 'HTTPError') {
                const {publicMessage} = await error.response.json()

                setApiErrorMsg(publicMessage)
            }
        }
    }

    return (
        <FormWrapper>
            <DynamicForm
                captchaAction="job_application_form"
                fields={fields}
                handleSubmit={submitForm}
                dataTestId="job-post-form"
                hasApiError={!!apiErrorMsg}
                submissionErrorMessage={apiErrorMsg}
            />
        </FormWrapper>
    )
}

enum GreenhouseFieldType {
    Text = 'input_text',
    File = 'input_file',
    Checkbox = 'multi_value_multi_select',
    Select = 'multi_value_single_select',
    Textarea = 'textarea',
}

type BasicFieldsType = Pick<InputProps, 'key' | 'label' | 'validators' | 'errorMessage' | 'width'>

const mapFields = (questions: GreenhouseQuestions, labels: GlobalStrings): FieldProps[] => {
    const halfWidthFields = ['first_name', 'last_name']

    const getFieldType = (field: GreenhouseQuestions[number]['fields'][number]) => {
        switch (field.type) {
            case GreenhouseFieldType.Text:
                /**
                 * Email and Phone inputs come as Text from Greenhouse
                 * So we need to catch those cases and replace it with our specific component
                 */
                if (field.name.includes('email')) {
                    return FieldType.Email
                }
                if (field.name.includes('phone')) {
                    return FieldType.Phone
                }
                return FieldType.Text
            case GreenhouseFieldType.Textarea:
                return FieldType.Textarea
            case GreenhouseFieldType.Select:
                return FieldType.Select
            case GreenhouseFieldType.Checkbox:
                return FieldType.Checkbox
            case GreenhouseFieldType.File:
                return FieldType.FileUpload
            default:
                throw new Error(`Field type not recognized >>>> ${field.type}`)
        }
    }

    return questions.map((question, i) => {
        const fieldType = getFieldType(question.fields[0])

        const basicFields: BasicFieldsType = {
            key: question.fields[0].name,
            label: question.label,
            validators: question.required ? ['isRequired'] : [],
            errorMessage: labels.this_field_is_required,
            width: halfWidthFields.includes(question.fields[0].name)
                ? FieldWidth.Half
                : FieldWidth.Full,
        }

        switch (fieldType) {
            case FieldType.Text:
            case FieldType.Textarea:
                return {
                    ...basicFields,
                    type: fieldType,
                }
            case FieldType.Email:
                return {
                    ...basicFields,
                    type: fieldType,
                    validators: ['isRequired', 'isValidEmail'],
                    errorMessage: labels.invalid_email_error,
                }
            case FieldType.Phone:
                return {
                    ...basicFields,
                    type: fieldType,
                    validators: ['isValidPhoneNumber'],
                }
            case FieldType.Checkbox:
                return {
                    ...basicFields,
                    placeholder: '',
                    type: fieldType,
                    options: question.fields[0].values.map((option) => ({
                        label: option.label,
                        value: option.value.toString(),
                    })),
                }
            case FieldType.Select:
                return {
                    ...basicFields,
                    placeholder: '',
                    type: fieldType,
                    options: question.fields[0].values.map((option) => ({
                        label: option.label,
                        value: option.value.toString(),
                    })),
                }
            case FieldType.FileUpload:
                return {
                    ...basicFields,
                    type: fieldType,
                    label: question.label,
                    id: `question-upload-${i}`,
                    allowedFileTypes: ['pdf', 'doc', 'docx', 'txt', 'rtf'],
                    uploadText: labels.upload_text,
                    fileTypesText: `(${labels.file_type}: pdf, doc, docx, txt, rtf), max 5MB`,
                    fileSize: 6,
                }
            default:
                throw new Error('Field type not recognized')
        }
    })
}

const MOBILE_BREAKPOINT = 'xs'

const FormWrapper = styled.div`
    ${container}
    display: grid;
    row-gap: ${({theme}) => theme.spacings.s32};
    width: 100%;
    padding: ${({theme}) => theme.spacings.s20};
    background: ${({theme}) => theme.colors.background.default};

    ${mq.min(MOBILE_BREAKPOINT)} {
        max-width: 600px;
        padding: ${({theme}) => `${theme.spacings.s28} ${theme.spacings.s40}`};
        border-radius: ${({theme}) => theme.radii.arc8};
    }
`
